import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FaChevronDown, FaRedo, FaRobot } from "react-icons/fa";
import { executionAssistService } from "../../../services/executionAssist.service";
import "../../workflow/aibuilder/AIBuilderWorkspace.css"; // shared .ai-chat-*/.ai-bubble-* styles
import "./GraceBotBubble.css";

const DRAG_MARGIN = 8; // keep the window at least this far from each viewport edge
const DRAG_THRESHOLD = 4; // px of movement before a press counts as a drag (vs a click)
const FAB_SIZE = 60; // must match .grace-bot-fab width/height
const PANEL_W = 400; // must match .grace-bot-panel's clamp() ceiling width
const PANEL_H = 520; // must match .grace-bot-panel's clamp() ceiling height
const MIN_W = 300; // minimum panel width when resizing — must match the clamp() floor
const MIN_H = 320; // minimum panel height when resizing — must match the clamp() floor

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// Mirrors .grace-bot-panel's CSS `clamp(MIN, vw/vh %, PANEL)` sizing in JS, so a position
// computed before the panel has ever rendered (nothing to measure yet) still matches what the
// CSS actually renders instead of assuming the fixed ceiling size unconditionally.
const defaultPanelSize = () => ({
  w: clamp(window.innerWidth * 0.3, MIN_W, PANEL_W),
  h: clamp(window.innerHeight * 0.42, MIN_H, PANEL_H),
});

// Given the icon's top-left, compute where the expanded panel's top-left should go so it
// "explodes" toward the open space — up-and-left when the icon is tucked bottom-right (the
// common case), so a wide panel never overflows off-screen. Returns null when the icon is at
// its default corner (panel then uses the CSS default bottom-right anchor).
function explodedPanelPos(iconPos, size) {
  if (!iconPos) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fallback = defaultPanelSize();
  const w = Math.min(size?.w || fallback.w, vw - 2 * DRAG_MARGIN);
  const h = Math.min(size?.h || fallback.h, vh - 2 * DRAG_MARGIN);
  const expandLeft = iconPos.x + FAB_SIZE / 2 > vw / 2;
  const expandUp = iconPos.y + FAB_SIZE / 2 > vh / 2;
  const x = expandLeft ? iconPos.x + FAB_SIZE - w : iconPos.x;
  const y = expandUp ? iconPos.y + FAB_SIZE - h : iconPos.y;
  return {
    x: clamp(x, DRAG_MARGIN, vw - w - DRAG_MARGIN),
    y: clamp(y, DRAG_MARGIN, vh - h - DRAG_MARGIN),
  };
}

// GraceBot — a draggable floating window over the Execution History Lines grid, mirroring
// CoryBot's (KbBuilderBubble) window chrome/drag/resize behavior. Unlike CoryBot this isn't a
// conversation: it's a single fetch-and-display of the execution-summary bot's plain-language
// summary for this execution, generated server-side from the execution's own history rows —
// the browser never sends error-line data or a question, and there's no conversationId.
function GraceBotBubble({ clientId, executionId, runbookId, runbookName }) {
  const [open, setOpen] = useState(false); // starts minimized to the bubble
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedForExecutionId, setLoadedForExecutionId] = useState(null);
  // Two independent positions (top-left, viewport px; null => default corner via CSS): the
  // minimized bubble and the open panel. Keeping them separate lets the bubble stay where you
  // left it while the panel explodes toward open space on open.
  const [iconPos, setIconPos] = useState(null);
  const [panelPos, setPanelPos] = useState(null);
  const [size, setSize] = useState(null); // {w, h} once resized; null => CSS default
  const windowRef = useRef(null);
  const dragOffset = useRef(null);
  const movedRef = useRef(false); // did the last press move far enough to be a drag?

  // A new execution means the cached summary (if any) is stale — drop it so reopening fetches
  // fresh rather than showing another execution's summary.
  useEffect(() => {
    setSummary(null);
    setError(null);
    setLoadedForExecutionId(null);
  }, [executionId]);

  const fetchSummary = useCallback(async () => {
    if (!executionId) {
      setError("Missing execution context — reload the page and try again.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await executionAssistService.getSummary(executionId, clientId);
      setSummary(data?.summary || "");
      setLoadedForExecutionId(executionId);
    } catch (err) {
      const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
      setError(serverMessage || err?.message || "Could not load a summary for this execution.");
    } finally {
      setLoading(false);
    }
  }, [executionId, clientId]);

  const handleDragStart = useCallback(
    (e) => {
      if (e.button !== 0 || !windowRef.current) return;
      const setPosition = open ? setPanelPos : setIconPos;
      const rect = windowRef.current.getBoundingClientRect();
      dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
      const startX = e.clientX;
      const startY = e.clientY;
      movedRef.current = false;
      e.preventDefault();

      const onMove = (ev) => {
        const off = dragOffset.current;
        const el = windowRef.current;
        if (!off || !el) return;
        if (!movedRef.current && Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > DRAG_THRESHOLD) {
          movedRef.current = true;
        }
        if (!movedRef.current) return;
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        const x = clamp(ev.clientX - off.dx, DRAG_MARGIN, window.innerWidth - w - DRAG_MARGIN);
        const y = clamp(ev.clientY - off.dy, DRAG_MARGIN, window.innerHeight - h - DRAG_MARGIN);
        setPosition({ x, y });
      };
      const onUp = () => {
        dragOffset.current = null;
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [open]
  );

  // Open from the bubble: explode the panel toward open space so it never renders off-screen,
  // and fetch the summary if we don't already have one for this execution.
  const openPanel = useCallback(() => {
    setPanelPos(explodedPanelPos(iconPos, size));
    setOpen(true);
    if (loadedForExecutionId !== executionId && !loading) {
      fetchSummary();
    }
  }, [iconPos, size, loadedForExecutionId, executionId, loading, fetchSummary]);

  // Resize from the top-left grip, keeping the panel's bottom-right corner fixed so it grows
  // up-and-left (matching its default bottom-right anchor).
  const handleResizeStart = useCallback((e) => {
    if (e.button !== 0 || !windowRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = windowRef.current.getBoundingClientRect();
    const brX = rect.right;
    const brY = rect.bottom;

    const onMove = (ev) => {
      const w = clamp(brX - ev.clientX, MIN_W, brX - DRAG_MARGIN);
      const h = clamp(brY - ev.clientY, MIN_H, brY - DRAG_MARGIN);
      setSize({ w, h });
      setPanelPos({ x: brX - w, y: brY - h });
    };
    const onUp = () => {
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const iconStyle = iconPos ? { left: iconPos.x, top: iconPos.y, right: "auto", bottom: "auto" } : undefined;
  const panelStyle =
    panelPos || size
      ? {
          ...(panelPos ? { left: panelPos.x, top: panelPos.y, right: "auto", bottom: "auto" } : {}),
          ...(size ? { width: size.w, height: size.h } : {}),
        }
      : undefined;

  if (!open) {
    return (
      <button
        type="button"
        ref={windowRef}
        className="grace-bot-fab"
        style={iconStyle}
        onMouseDown={handleDragStart}
        onClick={() => {
          // Suppress the open if the press was actually a drag.
          if (movedRef.current) {
            movedRef.current = false;
            return;
          }
          openPanel();
        }}
        title="GraceBot — click for an AI summary of this execution, drag to move"
        aria-label="Open GraceBot"
      >
        <FaRobot />
      </button>
    );
  }

  return (
    <div className="grace-bot-panel" ref={windowRef} style={panelStyle} role="dialog" aria-label="GraceBot">
      <div className="grace-bot-resize-handle" onMouseDown={handleResizeStart} title="Drag to resize" aria-hidden="true" />
      <div className="ai-builder-header grace-bot-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center grace-bot-drag-handle" onMouseDown={handleDragStart} title="Drag to move">
          <FaRobot className="grace-bot-avatar me-2" />
          <strong>GraceBot</strong>
        </div>
        <div className="d-flex align-items-center">
          <Button
            variant="link"
            size="sm"
            onClick={fetchSummary}
            disabled={loading}
            className="ai-builder-close p-0 me-3"
            title="Regenerate summary"
            aria-label="Regenerate summary"
          >
            <FaRedo />
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => setOpen(false)}
            className="ai-builder-close p-0"
            title="Minimize"
            aria-label="Minimize GraceBot"
          >
            <FaChevronDown />
          </Button>
        </div>
      </div>
      <div className="ai-chat-panel">
        <div className="ai-chat-scroll grace-bot-summary">
          {runbookName && <div className="grace-bot-summary-heading text-muted mb-2">{runbookName}</div>}
          {loading && (
            <div className="ai-chat-empty text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Summarizing this execution…
            </div>
          )}
          {!loading && error && (
            <div className="grace-bot-summary-error">
              {error}
              <div className="mt-2">
                <Button variant="link" size="sm" className="p-0" onClick={fetchSummary}>
                  Try again
                </Button>
              </div>
            </div>
          )}
          {!loading && !error && summary && (
            <div className="grace-bot-summary-text">{summary}</div>
          )}
          {!loading && !error && !summary && (
            <div className="ai-chat-empty text-muted">No summary available for this execution.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GraceBotBubble;
